'use client';

import { Mic, Video } from 'lucide-react';

import { type ComponentType, memo, useCallback } from 'react';

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';

import { type MediaDeviceOption, useMediaDevices } from '../hooks/use-media-devices';
import { useVideoRecording } from '../video-recording-provider';

type DeviceSelectProps = {
	icon: ComponentType<{ className?: string }>;
	label: string;
	value?: string;
	options: MediaDeviceOption[];
	disabled: boolean;
	onChange: (deviceId: string) => void;
};

const DeviceSelect = memo(function DeviceSelect({
	icon: Icon,
	label,
	value,
	options,
	disabled,
	onChange,
}: DeviceSelectProps) {
	// Value must match an option or Radix renders an empty trigger
	const selectedValue = options.some((option) => option.deviceId === value) ? value : undefined;

	return (
		<Select
			value={selectedValue}
			onValueChange={onChange}
			disabled={disabled || options.length <= 1}
		>
			<SelectTrigger
				aria-label={label}
				className="h-8 min-w-0 flex-1 gap-2 text-xs"
			>
				<Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
				<SelectValue placeholder={label} />
			</SelectTrigger>
			<SelectContent>
				{options.map((option) => (
					<SelectItem
						key={option.deviceId}
						value={option.deviceId}
						className="cursor-pointer text-xs hover:bg-accent"
					>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
});

/**
 * Mic and camera pickers shown before recording, so candidates can see and change which devices are used.
 */
export const DevicePickers = memo(function DevicePickers() {
	const { selectedDevices, switchDevice, uiState } = useVideoRecording();
	const { audioInputs, videoInputs } = useMediaDevices(uiState.cameraReady);

	const handleAudioChange = useCallback(
		(deviceId: string) => switchDevice('audio', deviceId),
		[switchDevice]
	);
	const handleVideoChange = useCallback(
		(deviceId: string) => switchDevice('video', deviceId),
		[switchDevice]
	);

	if (audioInputs.length === 0 && videoInputs.length === 0) return null;

	return (
		<div className="flex flex-col gap-2 sm:flex-row">
			<DeviceSelect
				icon={Mic}
				label="Microphone"
				value={selectedDevices.audioId}
				options={audioInputs}
				disabled={!uiState.cameraReady}
				onChange={handleAudioChange}
			/>
			<DeviceSelect
				icon={Video}
				label="Camera"
				value={selectedDevices.videoId}
				options={videoInputs}
				disabled={!uiState.cameraReady}
				onChange={handleVideoChange}
			/>
		</div>
	);
});
